import React, { useEffect, useMemo, useState } from "react";
import { presignUpload, uploadFileToS3 } from "./gameApi";
import { showValidationToast, showApiErrorToast } from "../../../utils/toastUtils";

import FormModal from "../../../components/form/FormModal";
import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./game.css";

const COUNTRY_OPTIONS = [
  { value: "IN", label: "India" },
  { value: "NP", label: "Nepal" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "AE", label: "UAE" },
];

const emptyForm = {
  name: "",
  iconUrl: "",
  iconKey: "",
  isGlobal: true,
  country: "",
};

const MAX_SIZE = 3 * 1024 * 1024;

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "image/avif",
];

const GameForm = ({
  open,
  mode = "create",
  initialData = null,
  onCancel,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setErrors({});
    setSelectedFile(null);
    setProgress(0);

    if (!initialData) {
      setForm(emptyForm);
      return;
    }

    setForm({
      name: initialData.name || "",
      iconUrl: initialData.iconUrl || "",
      iconKey: initialData.iconKey || "",
      isGlobal:
        typeof initialData.isGlobal === "boolean" ? initialData.isGlobal : true,
      country: initialData.country || "",
    });
  }, [initialData]);

  const preview = useMemo(() => String(form.iconUrl || "").trim(), [form.iconUrl]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleFilePick = (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      const message = "Unsupported file type";
      setErrors((prev) => ({ ...prev, iconUrl: message }));
      showValidationToast({ iconUrl: message });
      return;
    }

    if (file.size > MAX_SIZE) {
      const message = "File too large (max 3MB)";
      setErrors((prev) => ({ ...prev, iconUrl: message }));
      showValidationToast({ iconUrl: message });
      return;
    }

    setSelectedFile(file);
    clearError("iconUrl");
  };

  const doUpload = async () => {
    if (!selectedFile || uploading) return;

    setUploading(true);
    setProgress(0);

    try {
      const presign = await presignUpload({
        folder: "games",
        contentType: selectedFile.type,
        originalName: selectedFile.name,
        maxSizeBytes: MAX_SIZE,
      });

      const uploaded = await uploadFileToS3({
        presign,
        file: selectedFile,
        onProgress: (nextProgress) => setProgress(nextProgress),
      });

      setForm((prev) => ({
        ...prev,
        iconUrl: uploaded.publicUrl,
        iconKey: uploaded.key,
      }));

      setSelectedFile(null);
      setProgress(100);
      clearError("iconUrl");
    } catch (error) {
      console.error(error);
      const message = "Upload failed. Try again.";
      setErrors((prev) => ({ ...prev, iconUrl: message }));
      showApiErrorToast(error, message);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(form.name || "").trim()) {
      nextErrors.name = "Name is required";
    }

    const isGlobal = form.isGlobal ?? true;
    const country = String(form.country || "").trim();

    if (!isGlobal && !country) {
      nextErrors.country = "Country is required for non-global games";
    }

    if (mode === "create" && !form.iconKey) {
      nextErrors.iconUrl = "Please upload an icon";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showValidationToast(nextErrors);
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (uploading || submitting) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    const isGlobal = form.isGlobal ?? true;
    const country = String(form.country || "").trim();

    const payload = {
      name: form.name.trim(),
      iconUrl: String(form.iconUrl || "").trim(),
      iconKey: form.iconKey || undefined,
      isGlobal: !!isGlobal,
      ...(isGlobal ? {} : { country: country || undefined }),
    };

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (uploading || submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Create Game" : "Edit Game"}
      width="760px"
      submitting={uploading || submitting}
      submitText={mode === "create" ? "Create Game" : "Save Changes"}
      submittingText={uploading ? `Uploading ${progress || 0}%` : "Saving..."}
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <div className="game-form">
        <div className="game-grid">
          <FormField label="Game Name" error={errors.name}>
            <FormInput
              type="text"
              value={form.name}
              onChange={(event) => {
                set("name", event.target.value);
                clearError("name");
              }}
              placeholder="Football"
              required
            />
          </FormField>

          <FormField
            label="Scope"
            hint="Global games appear in all countries; country-specific games only in that country."
          >
            <FormSelect
              value={form.isGlobal ? "global" : "country"}
              onChange={(event) => {
                const isGlobal = event.target.value === "global";
                set("isGlobal", isGlobal);
                if (isGlobal) set("country", "");
                clearError("country");
              }}
            >
              <option value="global">Global</option>
              <option value="country">Country specific</option>
            </FormSelect>
          </FormField>

          {!form.isGlobal && (
            <FormField label="Country" error={errors.country}>
              <FormSelect
                value={form.country || ""}
                onChange={(event) => {
                  set("country", event.target.value);
                  clearError("country");
                }}
              >
                <option value="">Select country</option>
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          )}

          <FormField
            label="Game Icon"
            error={errors.iconUrl}
            hint="Upload an icon from your computer; it will be stored on AWS S3."
            className="game-span-2"
          >
            <div className="game-upload-row">
              <FormInput
                type="file"
                accept="image/*"
                onChange={(event) => handleFilePick(event.target.files?.[0])}
                disabled={uploading || submitting}
              />

              <FormButton
                type="button"
                variant="primary"
                onClick={doUpload}
                disabled={!selectedFile || uploading || submitting}
                className="game-upload-btn"
              >
                {uploading ? `Uploading ${progress || 0}%` : "Upload"}
              </FormButton>
            </div>

            {selectedFile && !uploading && (
              <small className="game-selected-file">Selected: {selectedFile.name}</small>
            )}

            <FormInput
              type="text"
              value={form.iconUrl}
              placeholder="S3 URL will appear here after upload"
              readOnly
              className="game-icon-url"
            />

            <input type="hidden" value={form.iconKey || ""} readOnly />
          </FormField>

          <FormField label="Preview" className="game-span-2">
            <div className="game-icon-preview">
              {preview ? (
                <img src={preview} alt="Game icon preview" />
              ) : (
                <div className="game-icon-placeholder">No icon yet</div>
              )}
            </div>
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default GameForm;